import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Tabs,
  Tab,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const Analytics = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [data, setData] = useState({
    ptScores: [],
    wrongAnswers: [],
    aiAnalysis: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch PT Scores
        const ptQuery = query(
          collection(db, 'ptScores'),
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc')
        );
        const ptDocs = await getDocs(ptQuery);
        const ptData = ptDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch Wrong Answer Journal
        const waQuery = query(
          collection(db, 'wrongAnswerJournal'),
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc')
        );
        const waDocs = await getDocs(waQuery);
        const waData = waDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        // Fetch AI Analysis
        const aiQuery = query(
          collection(db, 'aiAnalysis'),
          where('userId', '==', currentUser.uid),
          orderBy('timestamp', 'desc')
        );
        const aiDocs = await getDocs(aiQuery);
        const aiData = aiDocs.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setData({
          ptScores: ptData,
          wrongAnswers: waData,
          aiAnalysis: aiData,
        });
      } catch (error) {
        console.error('Error fetching analytics data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentUser]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderScoreProgress = () => {
    const scoreData = data.ptScores.map(pt => ({
      name: `PT ${pt.ptNumber}`,
      score: pt.score,
    })).reverse();

    return (
      <Box sx={{ height: 300, width: '100%', mb: 4 }}>
        <ResponsiveContainer>
          <LineChart data={scoreData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="name" />
            <YAxis domain={[0, 100]} />
            <Tooltip />
            <Line type="monotone" dataKey="score" stroke="#4F46E5" />
          </LineChart>
        </ResponsiveContainer>
      </Box>
    );
  };

  const renderWrongAnswerAnalysis = () => {
    const questionTypes = data.wrongAnswers.reduce((acc, wa) => {
      acc[wa.questionType] = (acc[wa.questionType] || 0) + 1;
      return acc;
    }, {});

    const pieData = Object.entries(questionTypes).map(([name, value]) => ({
      name,
      value,
    }));

    return (
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ height: 300 }}>
            <ResponsiveContainer>
              <PieChart>
                <Pie
                  data={pieData}
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                  label={({ name, value }) => `${name}: ${value}`}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </Box>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Recent Wrong Answers
              </Typography>
              <List>
                {data.wrongAnswers.slice(0, 5).map((wa, index) => (
                  <React.Fragment key={wa.id}>
                    <ListItem>
                      <ListItemText
                        primary={`PT ${wa.ptNumber} - Question ${wa.questionNumber}`}
                        secondary={wa.explanation}
                      />
                    </ListItem>
                    {index < 4 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderAIInsights = () => {
    return (
      <Grid container spacing={3}>
        {data.aiAnalysis.slice(0, 6).map((analysis) => (
          <Grid item xs={12} md={6} key={analysis.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {analysis.questionType}
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  {analysis.summary}
                </Typography>
                <Typography variant="subtitle2" color="primary">
                  Suggested Approach:
                </Typography>
                <Typography variant="body2">
                  {analysis.suggestedApproach}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom>
          Analytics & Insights
        </Typography>

        <Paper sx={{ mb: 3 }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab label="Score Progress" />
            <Tab label="Wrong Answer Analysis" />
            <Tab label="AI Insights" />
          </Tabs>
        </Paper>

        <Box sx={{ mt: 3 }}>
          {activeTab === 0 && renderScoreProgress()}
          {activeTab === 1 && renderWrongAnswerAnalysis()}
          {activeTab === 2 && renderAIInsights()}
        </Box>
      </Box>
    </Container>
  );
};

export default Analytics; 